import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft, Plus, Minus, ChevronRight, Search, Loader2, X } from 'lucide-react';
import { useEffectiveSerial } from '@/hooks/useEffectiveSerial';
import { useToast } from '@/hooks/use-toast';
import { useItunesSearch, ItunesApp } from '@/hooks/useItunesSearch';
import { useAppCategories } from '@/hooks/useAppCategories';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const AppList: React.FC = () => {
  const navigate = useNavigate();
  const { type } = useParams<{ type: 'whitelist' | 'blacklist' }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { serial } = useEffectiveSerial();
  const { toast } = useToast();
  const { allowedApps, blockedApps, isLoading: isLoadingApps } = useAppCategories();
  
  const [selectedApp, setSelectedApp] = useState<{ name: string; action: 'remove' | 'add' } | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItunesApp, setSelectedItunesApp] = useState<ItunesApp | null>(null);
  
  const { results: searchResults, isLoading: isSearching } = useItunesSearch(searchQuery);

  const isWhitelist = type === 'whitelist';
  const apps = isWhitelist ? allowedApps : blockedApps;
  const title = isWhitelist ? 'Whitelisted Apps' : 'Blacklisted Apps';
  const subtitle = isWhitelist ? 'Always allowed' : 'Always blocked';

  // Auto-open add modal if ?add=true query param is present
  useEffect(() => {
    if (searchParams.get('add') === 'true') {
      setShowSearchModal(true);
      // Remove the query param so it doesn't re-trigger
      searchParams.delete('add');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleAppTap = (appName: string) => {
    setSelectedApp({ name: appName, action: 'remove' });
    setShowConfirmation(true);
  };

  const handleAddRequest = () => {
    setShowSearchModal(true);
  };

  const handleSelectApp = (app: ItunesApp) => {
    setSelectedItunesApp(app);
    setShowSearchModal(false);
    setSelectedApp({ name: app.trackName, action: 'add' });
    setShowConfirmation(true);
  };

  const handleCloseSearchModal = () => {
    setShowSearchModal(false);
    setSearchQuery('');
    setSelectedItunesApp(null);
  };

  const handleConfirmRequest = () => {
    if (selectedApp) {
      const actionText = selectedApp.action === 'add' 
        ? `add "${selectedApp.name}" to the ${isWhitelist ? 'whitelist' : 'blocklist'}`
        : `remove "${selectedApp.name}" from the ${isWhitelist ? 'whitelist' : 'blocklist'}`;
      toast({
        title: "Request Submitted",
        description: `Your request to ${actionText} has been submitted for review.`,
      });
    }
    setShowConfirmation(false);
    setSelectedApp(null);
    setSelectedItunesApp(null);
    setSearchQuery('');
  };

  return (
    <div className="min-h-[100dvh] bg-gradient-to-b from-background to-muted/30 pb-24">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-lg border-b border-border/50">
        <div className="px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(`/${serial}/settings/app-management`)}
            className="p-2 -ml-2 rounded-full hover:bg-muted/50 active:scale-95 transition-all"
          >
            <ChevronLeft className="w-6 h-6 text-foreground" />
          </button>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${isWhitelist ? 'bg-primary/10' : 'bg-destructive/10'} flex items-center justify-center`}>
              {isWhitelist ? (
                <Plus className="w-5 h-5 text-primary" />
              ) : (
                <Minus className="w-5 h-5 text-destructive" />
              )}
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">{title}</h1>
              <p className="text-[13px] text-muted-foreground">{apps.length} apps • {subtitle}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 pt-4">
        <p className="text-[13px] text-muted-foreground mb-4 px-1">
          {isWhitelist 
            ? 'These apps are always available regardless of category settings. Tap an app to request removing it from the whitelist.'
            : 'These apps are always blocked regardless of category settings. Tap an app to request unblocking it.'}
        </p>

        <div className="bg-background rounded-2xl border border-border/50 shadow-sm overflow-hidden">
          <div className="divide-y divide-border/50">
            {isLoadingApps ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="px-4 py-4 flex items-center gap-3">
                  <Skeleton className="w-10 h-10 rounded-xl" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-28 mb-1.5" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
              ))
            ) : apps.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <p className="text-sm text-muted-foreground">
                  No {isWhitelist ? 'whitelisted' : 'blocked'} apps
                </p>
              </div>
            ) : (
              apps.map((app) => (
                <button
                  key={app.id}
                  onClick={() => handleAppTap(app.name)}
                  className="w-full px-4 py-4 flex items-center justify-between hover:bg-muted/30 active:scale-[0.99] transition-all text-left"
                >
                  <div className="flex items-center gap-3">
                    <img
                      src={app.iconUrl}
                      alt=""
                      className="w-10 h-10 rounded-xl object-cover bg-muted"
                      onError={(e) => { (e.target as HTMLImageElement).src = '/placeholder.svg'; }}
                    />
                    <div>
                      <p className="text-[15px] font-medium text-foreground">{app.name}</p>
                      {app.category && (
                        <p className="text-[12px] text-muted-foreground">{app.category}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] text-muted-foreground">Request Change</span>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </button>
              ))
            )}
            
            {/* Add App Button */}
            <button
              onClick={handleAddRequest}
              className="w-full px-4 py-4 flex items-center gap-3 text-primary hover:bg-muted/30 active:scale-[0.99] transition-all"
            >
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Plus className="w-5 h-5 text-primary" />
              </div>
              <span className="text-[15px] font-medium">
                Request to {isWhitelist ? 'Whitelist' : 'Block'} an App
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Search App Modal */}
      <Dialog open={showSearchModal} onOpenChange={setShowSearchModal}>
        <DialogContent className="max-w-[92vw] sm:max-w-md p-0 gap-0 rounded-2xl overflow-hidden">
          <DialogHeader className="px-5 pt-5 pb-4 border-b border-border/50">
            <DialogTitle className="text-lg font-semibold text-center">
              {isWhitelist ? 'Add App to Whitelist' : 'Add App to Blocklist'}
            </DialogTitle>
          </DialogHeader>
          
          {/* Search Input */}
          <div className="px-4 py-4">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <Input
                type="search"
                placeholder="Search for an app..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 bg-muted/50 border-border/50 rounded-xl text-[15px] placeholder:text-muted-foreground/60"
                autoFocus
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-muted"
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              )}
            </div>
          </div>

          {/* Results */}
          <div className="max-h-[50vh] overflow-y-auto px-4 pb-4">
            {searchQuery.length < 2 ? (
              <div className="py-12 text-center">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-muted/50 flex items-center justify-center mb-3">
                  <Search className="w-6 h-6 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Type at least 2 characters to search
                </p>
              </div>
            ) : isSearching ? (
              <div className="py-12 text-center">
                <Loader2 className="w-8 h-8 mx-auto text-primary animate-spin mb-3" />
                <p className="text-sm text-muted-foreground">Searching apps...</p>
              </div>
            ) : searchResults.length === 0 ? (
              <div className="py-12 text-center">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-muted/50 flex items-center justify-center mb-3">
                  <Search className="w-6 h-6 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground">
                  No apps found for "{searchQuery}"
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                {searchResults.map((app) => (
                  <button
                    key={app.trackId}
                    onClick={() => handleSelectApp(app)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 active:scale-[0.98] transition-all text-left"
                  >
                    <img
                      src={app.artworkUrl60}
                      alt={app.trackName}
                      className="w-12 h-12 rounded-xl object-cover bg-muted flex-shrink-0"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/placeholder.svg';
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{app.trackName}</p>
                      <p className="text-xs text-muted-foreground truncate">{app.artistName}</p>
                      <p className="text-xs text-muted-foreground/70">{app.primaryGenreName}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <AlertDialogContent className="max-w-[90vw] sm:max-w-md rounded-2xl bg-background">
          {selectedItunesApp && (
            <div className="flex justify-center mb-4">
              <img
                src={selectedItunesApp.artworkUrl100}
                alt={selectedItunesApp.trackName}
                className="w-20 h-20 rounded-2xl object-cover shadow-lg"
              />
            </div>
          )}
          <AlertDialogHeader>
            <AlertDialogTitle className="text-center">
              {selectedApp?.action === 'add' 
                ? `${isWhitelist ? 'Whitelist' : 'Block'} "${selectedApp?.name}"?`
                : `Remove "${selectedApp?.name}"?`}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[14px] text-center">
              {selectedApp?.action === 'add' 
                ? isWhitelist 
                  ? 'This app will always be available once your request is approved.'
                  : 'This app will be blocked once your request is approved.'
                : isWhitelist
                  ? `This app's availability will depend on its category settings.`
                  : `This app will become available based on its category settings.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-col">
            <AlertDialogAction 
              onClick={handleConfirmRequest}
              className="w-full rounded-xl bg-primary text-primary-foreground h-12 text-[15px] font-semibold"
            >
              Submit Request
            </AlertDialogAction>
            <AlertDialogCancel className="w-full rounded-xl h-12 text-[15px] mt-0">
              Cancel
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AppList;